import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useSettings } from "@/lib/useSettings";
import { useEffect, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { getPublishedServices } from "@/api/services";

export function Header() {
  const { lang, setLang, t } = useLang();
  const { settings } = useSettings();
  const company = settings.company ?? {};
  const headerSettings = settings.header ?? {};
  const hero = settings.hero ?? {};
  const [services, setServices] = useState<any[]>([]);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobile, setMobile] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [langDropdown, setLangDropdown] = useState(false);
  
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

  useEffect(() => {
    getPublishedServices()
      .then(data => setServices(data ?? []))
      .catch(err => console.error('Failed to load services:', err));
  }, []);

  const menuStyle = {
    fontFamily: headerSettings.fontFamily || "inherit",
    fontSize: headerSettings.fontSize || "15px",
    fontWeight: headerSettings.fontWeight || "500",
  };

  const defaultMenu = [
    { id: "1", title_tr: "Anasayfa", title_en: "Home", path: "/", type: "link" },
    { id: "2", title_tr: "Hakkımızda", title_en: "About Us", path: "/about", type: "link" },
    { id: "3", title_tr: "Hizmetlerimiz", title_en: "Our Services", path: "/services", type: "link" },
    { id: "4", title_tr: "Duyurular", title_en: "Announcements", path: "/announcements", type: "link" },
    { id: "5", title_tr: "İletişim", title_en: "Contact", path: "/contact", type: "link" }
  ];

  const menuItems = settings.menu && Array.isArray(settings.menu) && settings.menu.length > 0 ? settings.menu : defaultMenu;

  return (
    <header className="bg-white sticky top-0 z-50 border-b shadow-sm" style={{ height: headerSettings.height || "90px" }}>
      <div className="container mx-auto h-full px-4 flex justify-between items-center" style={{ color: "#1c6ca3" }}>
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/awork1_logo.png"
            alt={company.name ?? "Awork"} 
            style={{ height: headerSettings.logoHeight || "70px" }} 
            className="w-auto object-contain max-h-[55px] md:max-h-none transition-all" 
          />
        </Link>

        <nav className="hidden md:flex items-center justify-center gap-6" style={menuStyle}>
          {menuItems.map((item: any) => {
            const label = item[lang === "tr" ? "title_tr" : "title_en"];
            if (item.type === "link") {
              return (
                <Link key={item.id} to={item.path} className="hover:opacity-70 transition-opacity whitespace-nowrap">
                  {label}
                </Link>
              );
            }

            // Megamenu / Dropdown
            const isDropdownOpen = activeDropdown === item.id;
            const hasItems = item.items && Array.isArray(item.items) && item.items.length > 0;
            
            return (
              <div
                key={item.id}
                className="relative"
                onMouseEnter={() => setActiveDropdown(item.id)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button className="flex items-center gap-1 hover:opacity-70 transition-opacity cursor-pointer whitespace-nowrap">
                  {label} {hasItems && <ChevronDown size={14} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />}
                </button>
                {isDropdownOpen && hasItems && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
                    <div className="bg-white shadow-xl border border-solid p-2 min-w-[320px] w-max flex flex-col gap-0.5 animate-fade-in-up">
                      {(item.items || []).map((sub: any, sIdx: number) => {
                        const subLabel = sub[lang === "tr" ? "title_tr" : "title_en"];
                        const subDesc = sub[lang === "tr" ? "desc_tr" : "desc_en"];
                        return (
                          <Link
                            key={sIdx}
                            to={sub.path}
                            className="group block px-4 py-2.5 hover:bg-primary/5 transition-all duration-200 border border-transparent hover:border-primary/10 text-left"
                          >
                            <div className="font-bold text-sm text-primary group-hover:text-primary-dark transition-colors whitespace-nowrap">
                              {subLabel}
                            </div>
                            {subDesc && (
                              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed group-hover:text-muted-foreground/80 whitespace-normal max-w-xs">
                                {subDesc}
                              </p>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="flex items-center justify-end gap-2">
          <div 
            className="relative hidden md:block"
            onMouseEnter={() => setLangDropdown(true)}
            onMouseLeave={() => setLangDropdown(false)}
          >
            <button className="flex items-center gap-2 px-3 py-2 hover:bg-accent rounded transition-colors">
              <span className="text-xl">{lang === "tr" ? "🇹🇷" : "🇬🇧"}</span>
              <span className="text-sm font-medium">{lang === "tr" ? "TR" : "EN"}</span>
              <ChevronDown size={14} className={`transition-transform duration-200 ${langDropdown ? 'rotate-180' : ''}`} />
            </button>
            {langDropdown && (
              <div className="absolute top-full right-0 pt-1 z-50">
                <div className="bg-white shadow-xl border p-1 min-w-[140px] flex flex-col gap-0.5 animate-fade-in-up">
                  <button
                    onClick={() => { setLang("tr"); setLangDropdown(false); }}
                    className={`flex items-center gap-2 px-3 py-2 hover:bg-primary/5 transition-colors text-left ${lang === "tr" ? "bg-primary/10" : ""}`}
                  >
                    <span className="text-xl">🇹🇷</span>
                    <span className="text-sm font-medium">Türkçe</span>
                  </button>
                  <button
                    onClick={() => { setLang("en"); setLangDropdown(false); }}
                    className={`flex items-center gap-2 px-3 py-2 hover:bg-primary/5 transition-colors text-left ${lang === "en" ? "bg-primary/10" : ""}`}
                  >
                    <span className="text-xl">🇬🇧</span>
                    <span className="text-sm font-medium">English</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          <button className="md:hidden ml-2 p-2" onClick={() => setMobile(true)}><Menu /></button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobile && (
        <div 
          className="fixed inset-0 z-[100] bg-black/50 md:hidden transition-opacity" 
          onClick={() => setMobile(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div 
        className={`fixed top-0 left-0 h-full w-[80%] bg-white z-[101] shadow-2xl transform transition-transform duration-300 ease-in-out md:hidden flex flex-col ${mobile ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ color: "#1c6ca3" }}
      >
        <div className="p-4 flex justify-between items-center border-b">
          <img 
            src="/awork1_logo.png"
            alt={company.name ?? "Awork"} 
            className="h-8 w-auto object-contain" 
          />
          <button onClick={() => setMobile(false)} className="p-2 hover:bg-accent rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto px-4 py-6 flex flex-col gap-2" style={menuStyle}>
            {menuItems.map((item: any) => {
              const label = item[lang === "tr" ? "title_tr" : "title_en"];
              const hasItems = item.items && Array.isArray(item.items) && item.items.length > 0;
              
              if (item.type === "link" && !hasItems) {
                return (
                  <Link key={item.id} to={item.path} onClick={() => setMobile(false)} className="py-2 hover:opacity-70 font-medium text-lg">
                    {label}
                  </Link>
                );
              }

              if (!hasItems) return null;

              const isExpanded = mobileExpanded === item.id;

              return (
                <div key={item.id} className="flex flex-col">
                  <button 
                    onClick={() => setMobileExpanded(isExpanded ? null : item.id)}
                    className="py-2 hover:opacity-70 font-medium text-lg flex items-center justify-between text-left"
                  >
                    {label}
                    <ChevronDown size={18} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  {isExpanded && (
                    <div className="flex flex-col gap-1 pl-4 mt-1 border-l-2 border-primary/20 animate-fade-in-up">
                      {(item.items || []).map((sub: any, sIdx: number) => {
                        const subLabel = sub[lang === "tr" ? "title_tr" : "title_en"];
                        return (
                          <Link key={sIdx} to={sub.path} onClick={() => setMobile(false)} className="py-1.5 text-sm opacity-90 hover:opacity-100">
                            {subLabel}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        <div className="p-4 border-t mt-auto">
          <div className="flex gap-2">
            <button
              onClick={() => { setLang("tr"); setMobile(false); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded-md transition-colors ${lang === "tr" ? "bg-primary/10 border-primary" : "hover:bg-accent"}`}
            >
              <span className="text-xl">🇹🇷</span>
              <span className="text-sm font-medium">TR</span>
            </button>
            <button
              onClick={() => { setLang("en"); setMobile(false); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded-md transition-colors ${lang === "en" ? "bg-primary/10 border-primary" : "hover:bg-accent"}`}
            >
              <span className="text-xl">🇬🇧</span>
              <span className="text-sm font-medium">EN</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
